import type { NativeSelectOption } from '@shared/ui/fields'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { LessonActionModalContext } from './interfaces'
import type { LessonStatusReasons } from './lesson-status'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'
import { ReasonCategory, Status as BookingStatus, Type as BookingType } from '@brighterly/lib-core-types/booking/enums'

import { useProfileStore } from '@entities/viewer'
import { useListsStore } from '@shared/model'
import { useDayjs } from '@shared/lib/format'
import { useToast } from '@shared/ui/toast'

import { useDisciplineStore } from '../store'
import { useLessonRescheduleApi } from '../../api/lessons/reschedule.hook'
import { useLessonTutorAvailability } from './lesson-tutor-availability.hook'
import {
  LESSON_RESCHEDULE_STATUSES,
  buildLessonStatusOptions,
  buildReasonCategoryOptions,
  buildReasonOptions,
  getFirstReason,
  isLessonReasonStatus,
} from './lesson-status'

interface ModalLessonRescheduleReturn {
  refStatusOptions: ComputedRef<NativeSelectOption[]>
  refStatus: Ref<BookingStatus>
  refCategoryOptions: ComputedRef<NativeSelectOption[]>
  refCategory: Ref<string | null>
  refReasonOptions: ComputedRef<NativeSelectOption[]>
  refReason: Ref<string | null>
  refDate: Ref<string>
  refMinDate: ComputedRef<string>
  refStartOptions: ComputedRef<NativeSelectOption[]>
  refStartTime: Ref<string>
  refDurationOptions: ComputedRef<NativeSelectOption[]>
  refDuration: Ref<number>
  refTutorId: Ref<number | null>
  refTutorOptions: ComputedRef<AppSelectOption<number>[]>
  refIsTutorsLoading: Ref<boolean>
  refIsSaving: Ref<boolean>
  setStatus: (option: NativeSelectOption) => void
  setCategory: (option: NativeSelectOption) => void
  setReason: (option: NativeSelectOption) => void
  setDate: (value: string) => void
  setStartTime: (option: NativeSelectOption) => void
  setDuration: (option: NativeSelectOption) => void
  close: () => void
  confirm: () => Promise<void>
}

const withCurrent = <T extends string | number>(values: T[], current: T): T[] =>
  values.includes(current) ? values : [current, ...values]

/** Closes the lesson given as rescheduled and books its replacement in one call. The modal stays open under its
 *  loader until the page's follow-up is done, and closes at once on a refusal. */
export const useModalLessonReschedule = (context: LessonActionModalContext): ModalLessonRescheduleReturn => {
  const route = useRoute()
  const toast = useToast()
  const dayjs = useDayjs()
  const listsStore = useListsStore()
  const profileStore = useProfileStore()
  const disciplineStore = useDisciplineStore()

  const { lesson, slots } = context
  const start = dayjs(lesson.start, DateFormat.Default)

  const reasons = computed<LessonStatusReasons>(() =>
    (listsStore.getLists?.booking.change_status_reasons ?? {}) as LessonStatusReasons)

  const hasReason = isLessonReasonStatus(lesson.status)
  const initialCategory = hasReason && lesson.reason_category ? lesson.reason_category : ReasonCategory.Admin
  const initialStatus = LESSON_RESCHEDULE_STATUSES.includes(lesson.status) ? lesson.status : BookingStatus.Rescheduled

  const refStatus = ref<BookingStatus>(initialStatus)
  const refCategory = ref<string | null>(initialCategory)
  const refReason = ref<string | null>(hasReason && lesson.reason ? lesson.reason : getFirstReason(reasons.value, initialCategory))
  const refDate = ref<string>(start.isValid() ? start.format(DateFormat.DefaultDate) : '')
  const refStartTime = ref<string>(start.isValid() ? start.format(DateFormat.DefaultTime) : '')
  const refDuration = ref<number>(lesson.duration ?? slots?.duration[0] ?? 0)
  const refTutorId = ref<number | null>(lesson.tutor_id ?? null)
  const refIsSaving = ref<boolean>(false)

  const refMinDate = computed<string>(() => {
    const now = profileStore.getCurrentTime
    return now ? dayjs(now, DateFormat.Default).format(DateFormat.DefaultDate) : ''
  })

  const refStatusOptions = computed<NativeSelectOption[]>(() =>
    buildLessonStatusOptions(listsStore.getLists?.booking.status ?? {}, LESSON_RESCHEDULE_STATUSES, refStatus.value))

  const refCategoryOptions = computed<NativeSelectOption[]>(() =>
    buildReasonCategoryOptions(reasons.value, listsStore.getLists?.booking.reason_category ?? {}, refCategory.value))

  const refReasonOptions = computed<NativeSelectOption[]>(() =>
    buildReasonOptions(reasons.value, refCategory.value, refReason.value))

  const refStartOptions = computed<NativeSelectOption[]>(() =>
    withCurrent(slots?.start ?? [], refStartTime.value).map(slot => ({
      id: slot,
      value: slot,
      text: slot,
      selected: slot === refStartTime.value,
    })))

  const refDurationOptions = computed<NativeSelectOption[]>(() =>
    withCurrent(slots?.duration ?? [], refDuration.value).map(minutes => ({
      id: minutes,
      value: minutes,
      text: `${minutes} min`,
      selected: minutes === refDuration.value,
    })))

  const refStart = computed<string>(() => (refDate.value && refStartTime.value ? `${refDate.value} ${refStartTime.value}:00` : ''))

  const refTutorModel = computed<number | null>({
    get: () => refTutorId.value,
    set: (value) => {
      refTutorId.value = value
    },
  })

  const { refOptions: refTutorOptions, refIsLoading: refIsTutorsLoading } = useLessonTutorAvailability({
    disciplineId: Number(route.params.id),
    refStart,
    refType: computed(() => lesson.type),
    refFilters: computed(() => null),
    refTutorId: refTutorModel,
    bookingId: lesson.id,
    currentTutor: lesson.tutor ?? null,
  })

  const setStatus = (option: NativeSelectOption): void => {
    refStatus.value = option.value as BookingStatus
  }

  const setCategory = (option: NativeSelectOption): void => {
    refCategory.value = option.value ? String(option.value) : null
    refReason.value = getFirstReason(reasons.value, refCategory.value)
  }

  const setReason = (option: NativeSelectOption): void => {
    refReason.value = option.value ? String(option.value) : null
  }

  const setDate = (value: string): void => {
    if (value) refDate.value = value
  }

  const setStartTime = (option: NativeSelectOption): void => {
    refStartTime.value = String(option.value)
  }

  const setDuration = (option: NativeSelectOption): void => {
    refDuration.value = Number(option.value)
  }

  const close = (): void => {
    if (refIsSaving.value) return
    context.close()
  }

  const confirm = async (): Promise<void> => {
    if (refIsSaving.value || !refStart.value || !refCategory.value) return

    // A bad reschedule of a paid lesson takes a new lesson from the balance; the legacy modal warned first.
    const balance = disciplineStore.getDetails?.unscheduled_bookings ?? null
    const isPaidBad = lesson.type === BookingType.Paid && refStatus.value === BookingStatus.RescheduledBad

    if (isPaidBad && balance !== null && balance <= 0) {
      toast.warning({
        title: `Lessons available for booking is: ${balance}`,
        description: 'Check the Balances page table!',
      })
      return
    }

    refIsSaving.value = true

    const replacement = await useLessonRescheduleApi({
      disciplineId: route.params.id as string,
      lessonId: lesson.id,
      payload: {
        start: refStart.value,
        duration: refDuration.value,
        status: refStatus.value,
        reason_category: refCategory.value,
        reason: refReason.value,
        tutor_id: refTutorId.value,
      },
    })

    // The page's follow-up runs before the modal closes, under the modal's loader; a refusal closes it at once.
    if (replacement) await context.afterSave()

    refIsSaving.value = false
    context.close()
  }

  return {
    refStatusOptions,
    refStatus,
    refCategoryOptions,
    refCategory,
    refReasonOptions,
    refReason,
    refDate,
    refMinDate,
    refStartOptions,
    refStartTime,
    refDurationOptions,
    refDuration,
    refTutorId,
    refTutorOptions,
    refIsTutorsLoading,
    refIsSaving,
    setStatus,
    setCategory,
    setReason,
    setDate,
    setStartTime,
    setDuration,
    close,
    confirm,
  }
}
